import React from 'react';
import { Layout } from '../components/Layout';
import { CalendarHeader } from '../components/Calendar/CalendarHeader';
import { CalendarGrid } from '../components/Calendar/CalendarGrid';
import { CalendarSync } from '../components/Calendar/CalendarSync';
import { MoodProvider } from '../contexts/MoodContext';
import { Calendar as CalendarIcon, LayoutGrid, Calendar as CalendarWeekIcon } from 'lucide-react';
import type { Task } from '../types';

export function Calendar() {
  const [currentDate, setCurrentDate] = React.useState(new Date());
  const [view, setView] = React.useState<'month' | 'week'>('month');
  const [showSyncSettings, setShowSyncSettings] = React.useState(false);
  const [tasks] = React.useState<Task[]>([
    {
      id: '1',
      title: 'Team Meeting',
      description: 'Weekly sync with the design team',
      role: 'work',
      priority: 'high',
      status: 'todo',
      dueDate: new Date(),
    },
    {
      id: '2',
      title: 'Grocery Shopping',
      description: 'Buy weekly groceries',
      role: 'family',
      priority: 'medium',
      status: 'todo',
      dueDate: new Date(Date.now() + 86400000), // Tomorrow
    },
  ]);

  React.useEffect(() => {
    // Handle OAuth token response
    const hash = window.location.hash;
    if (hash) {
      const params = new URLSearchParams(hash.substring(1));
      const accessToken = params.get('access_token');
      if (accessToken) {
        // Store the access token
        localStorage.setItem('google_calendar_token', accessToken);
        // Clear hash from URL
        window.history.replaceState({}, '', window.location.pathname);
        // Show success message or update UI
        console.log('Successfully connected to Google Calendar');
      }
    }
  }, []);

  const handlePrevMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1));
  };

  return (
    <Layout>
      <MoodProvider>
        <div className="space-y-6">
          <div className="flex items-center justify-between gap-4">
            <CalendarHeader
              currentDate={currentDate}
              onPrevMonth={handlePrevMonth}
              onNextMonth={handleNextMonth}
            />
            <div className="flex items-center gap-2">
              <div className="bg-[#2A2B2E] rounded-xl p-1 flex items-center gap-1">
                <button
                  onClick={() => setView('month')}
                  className={`p-2 rounded-lg transition-colors ${
                    view === 'month' ? 'bg-[var(--accent-color)] text-black' : 'hover:bg-black/20'
                  }`}
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setView('week')}
                  className={`p-2 rounded-lg transition-colors ${
                    view === 'week' ? 'bg-[var(--accent-color)] text-black' : 'hover:bg-black/20'
                  }`}
                >
                  <CalendarWeekIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
          <CalendarGrid 
            currentDate={currentDate} 
            view={view}
            tasks={tasks}
            onPrevious={handlePrevMonth}
            onNext={handleNextMonth}
          />
        </div>
        {showSyncSettings && (
          <CalendarSync onClose={() => setShowSyncSettings(false)} />
        )}
      </MoodProvider>
    </Layout>
  );
}